import { formatNumber } from '@folio/lib';
import { QuoteContext } from '@folio/modules/shared-quote/QuoteContext';
import type { CustomIncludedPerilsProps } from 'korint-sdk';
import { keyValueFieldsToObject } from 'korint-sdk';
import { CheckIcon } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const tierPercentages: Record<string, number> = {
  BASIC: 0.3,
  COMFORT: 0.5,
  PREMIUM: 0.8,
};

function formatEuro(value: number | undefined): string | undefined {
  return value != null ? `${formatNumber(value)} €` : undefined;
}

const T_PREFIX = 'tutassurHiscoxMultirisquesPro.guaranteeLevels';

export function IncludedPerilsMRP({ tier }: CustomIncludedPerilsProps) {
  const { t } = useTranslation();
  const { asset, calculatedQuotes } = useContext(QuoteContext);

  const policyOutputs = useMemo(
    () =>
      keyValueFieldsToObject(
        calculatedQuotes.find(q => q.tier === tier)?.quote!.outputCustomFields
          ?.policyCustomFields ?? [],
      ),
    [calculatedQuotes],
  );

  const contentValue = useMemo(
    () => Number(asset?.customFields?.assetContentValue) || undefined,
    [asset],
  );
  const facilitiesValue = useMemo(
    () => Number(policyOutputs.FacilitiesLimitOfIndemnity) || undefined,
    [policyOutputs],
  );
  const financialLossValue = useMemo(
    () => Number(policyOutputs.FinancialLossLimitOfIndemnity) || undefined,
    [policyOutputs],
  );

  const percentage = tier ? tierPercentages[tier] : undefined;
  const percentageContentValue =
    contentValue && percentage ? Math.round(contentValue * percentage) : undefined;

  const guarantees: Array<{
    label: string;
    description: string;
    footnote?: string;
  }> = [
    {
      label: t(`${T_PREFIX}.fire`),
      description: formatEuro(contentValue) ?? t(`${T_PREFIX}.insuredContent`),
      footnote: '*',
    },
    {
      label: t(`${T_PREFIX}.waterDamage`),
      description:
        formatEuro(percentageContentValue) ??
        t(`${T_PREFIX}.percentContent`, {
          percentage: percentage ? Math.round(percentage * 100) : '',
        }),
    },
    {
      label: t(`${T_PREFIX}.glassBreakage`),
      description: formatEuro(facilitiesValue) ?? t(`${T_PREFIX}.insuredFacilities`),
    },
    {
      label: t(`${T_PREFIX}.theft`),
      description:
        formatEuro(percentageContentValue) ??
        t(`${T_PREFIX}.percentContent`, {
          percentage: percentage ? Math.round(percentage * 100) : '',
        }),
      footnote: '**',
    },
    {
      label: t(`${T_PREFIX}.vandalism`),
      description: formatEuro(contentValue) ?? t(`${T_PREFIX}.insuredContent`),
      footnote: '*',
    },
  ];

  if (financialLossValue !== undefined) {
    guarantees.push(
      {
        label: t(`${T_PREFIX}.lossOfProfit`),
        description: formatEuro(financialLossValue) ?? t(`${T_PREFIX}.financialGuarantee`),
      },
      {
        label: t(`${T_PREFIX}.additionalExpenses`),
        description: formatEuro(financialLossValue) ?? t(`${T_PREFIX}.financialGuarantee`),
      },
    );
  }

  return (
    <Stack gap={2} className="text-sm">
      <div className="mb-1 font-bold">{t(`${T_PREFIX}.title`)}</div>
      <ul className="flex flex-col gap-1.5">
        {guarantees.map(g => (
          <li key={g.label} className="flex items-start gap-2">
            <CheckIcon className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <Stack gap={0}>
              <span>
                {g.label}
                {g.footnote && <sup className="text-xs text-gray-500"> {g.footnote}</sup>}
              </span>
              <Text inline size="xs" className="text-gray-500">
                {g.description}
              </Text>
            </Stack>
          </li>
        ))}
      </ul>
      <Stack className="mt-2 text-xs text-gray-400 gap-0.5">
        <div>* {t(`${T_PREFIX}.contentFootnote`)}</div>
        <div>** {t(`${T_PREFIX}.mainFootnote`)}</div>
      </Stack>
    </Stack>
  );
}
